'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { DEFAULT_SEMESTER_ID } from '@/app/lib/semesters';
import { fetchExploreCourseDetails } from '@/app/services/courseCatalogClient';
import { toExploreCourseData } from '@/app/explore/courseData';
import Modal from '@/app/Components/Modal';
import { Button } from '@/app/Components/Button';
import { OperationalState } from '@/app/Components/OperationalPage';
import { useAuth } from '@/app/Components/AuthProvider';
import { loadUserAcademicData } from '@/app/services/userAcademicData';
import type { ExploreCourseData, RutgersCourse, UserAPCredit } from '@/app/types/interfaces';
import type { DegreeCourseListDirectRow } from '@/app/services/degreeRequirementService';
import type { OpenCourseRequest } from './types';

const CourseDetailsModal = dynamic(() => import('@/app/explore/CourseDetailsModal'), { ssr: false });

type CoursePeekValue = { openCourse: (request: OpenCourseRequest) => void };

const CoursePeekContext = createContext<CoursePeekValue | null>(null);

/** The catalog keys courses by 8 digits; a bare subject:number is New Brunswick (01). */
export function toCatalogCourseId(value: string) {
  const digits = value.replace(/\D/g, '');
  return digits.length === 6 ? `01${digits}` : digits.length >= 8 ? digits.slice(0, 8) : value.trim();
}

/** Null outside the provider so callers can fall back to a link to Explore. */
export function useCoursePeek() {
  return useContext(CoursePeekContext);
}

type PeekState =
  | { status: 'closed' }
  | { status: 'loading' | 'error'; request: OpenCourseRequest; semester: string }
  | { status: 'ready'; request: OpenCourseRequest; semester: string; course: ExploreCourseData; degreeLists?: Map<string, DegreeCourseListDirectRow>; completed: RutgersCourse[]; apCredits: UserAPCredit[] };

/**
 * One course view for the whole app. Any page can open a course by id and pass
 * what it knows about it; the view loads the same selected-course record
 * Explore uses and leads with that page context.
 */
export function CoursePeekProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const [state, setState] = useState<PeekState>({ status: 'closed' });
  const [attempt, setAttempt] = useState(0);

  const openCourse = useCallback((request: OpenCourseRequest) => {
    setState({ status: 'loading', request: { ...request, courseId: toCatalogCourseId(request.courseId) }, semester: request.semester ?? DEFAULT_SEMESTER_ID });
  }, []);
  const close = useCallback(() => setState({ status: 'closed' }), []);

  const loadingKey = state.status === 'loading' ? `${state.semester}|${state.request.courseId}|${attempt}` : null;
  useEffect(() => {
    if (state.status !== 'loading') return;
    const controller = new AbortController();
    const { request, semester } = state;
    void Promise.all([
      fetchExploreCourseDetails(semester, request.courseId, controller.signal),
      import('@/app/services/degreeDataService').then((module) => module.loadDegreeCourseListsDirect()).catch(() => undefined),
      // Prerequisite status is personal; signed-out students see the general rule.
      userId ? loadUserAcademicData(userId).catch(() => null) : Promise.resolve(null),
    ]).then(([detail, degreeLists, academic]) => {
      if (!controller.signal.aborted) setState({ status: 'ready', request, semester, course: toExploreCourseData(detail.summary, detail.sections), degreeLists, completed: academic?.completedCourses ?? [], apCredits: academic?.apCredits ?? [] });
    }).catch(() => {
      if (!controller.signal.aborted) setState({ status: 'error', request, semester });
    });
    return () => controller.abort();
    // loadingKey captures every input that should restart the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingKey]);

  const value = useMemo(() => ({ openCourse }), [openCourse]);

  return (
    <CoursePeekContext.Provider value={value}>
      {children}
      {state.status === 'ready' ? (
        <CourseDetailsModal
          semester={state.semester}
          course={state.course}
          onClose={close}
          pageContext={state.request.context}
          degreeCourseListsDirect={state.degreeLists}
          userCompletedCourses={state.completed}
          userAPCredits={state.apCredits}
          onSelectCourse={(course) => openCourse({ courseId: course.baseId, semester: state.semester })}
        />
      ) : null}
      {state.status === 'loading' || state.status === 'error' ? (
        <Modal isOpen onClose={close} title={state.request.courseId} size="md" ariaLabel="Course details">
          {state.status === 'loading'
            ? <OperationalState kind="loading" title="Loading course details" />
            : <OperationalState
                kind="error"
                title="Course details unavailable"
                description="This course isn't in the selected term's catalog, or the catalog couldn't load."
                action={<Button onClick={() => { setAttempt((value) => value + 1); setState({ status: 'loading', request: state.request, semester: state.semester }); }}>Try again</Button>}
              />}
        </Modal>
      ) : null}
    </CoursePeekContext.Provider>
  );
}
